<template>
  <WizardContainer
    width="792px"
    height="624px"
  >
    <WizardContent>
      <template #flashMessage>
        <EduWarningFlashMessage :email="email" />
      </template>
      <h4 class="my-6">
        Request a Smart Editor Demo
      </h4>
      <p class="paragraph mb-6">
        Learn how your team can use augmented writing to write job descriptions that are proven
        to attract more qualified and diverse talent pools.
      </p>
      <v-form
        ref="form"
        v-model="valid"
        class="form"
        @submit.prevent="submitForm"
      >
        <DpplTextField
          v-model="firstName"
          label="First Name"
          :rules="firstNameRules"
          validate-on="blur"
        />
        <DpplTextField
          v-model="lastName"
          label="Last Name"
          :rules="lastNameRules"
          validate-on="blur"
        />
        <DpplTextField
          v-model="email"
          class="mb-3"
          label="Company Email"
          :rules="emailRules"
          validate-on="blur"
        />
        <DpplSignupFormButton
          type="submit"
          block
          :loading="loading"
        >
          REQUEST A DEMO
        </DpplSignupFormButton>
      </v-form>
    </WizardContent>
  </WizardContainer>
</template>

<script>
import WizardContainer from '@/components/shared/WizardContainer.vue';
import WizardContent from '@/components/shared/WizardContent.vue';
import EduWarningFlashMessage from '@/components/shared/EduWarningFlashMessage.vue';
import { signupCreateLeadUserDemo } from '@/api/signup';
import validation from '@/helpers/validation';
import DpplTextField from '@/components/datapeople/DpplTextField.vue';
import DpplSignupFormButton from '@/components/datapeople/DpplSignupFormButton.vue';
import { mapActions } from 'pinia';
import { useWizard } from '@/stores/wizard/wizard';

export default {
  name: 'DemoRequest',
  components: {
    WizardContainer,
    WizardContent,
    DpplTextField,
    DpplSignupFormButton,
    EduWarningFlashMessage,
  },
  emits: ['track-page-load'],
  data() {
    return {
      firstName: '',
      firstNameRules: [
        (v) => !validation.isEmpty(v) || 'First name is required',
        (v) => (v && v.length <= 250) || 'First name is too long',
      ],
      lastName: '',
      lastNameRules: [
        (v) => !validation.isEmpty(v) || 'Last name is required',
        (v) => (v && v.length <= 250) || 'last name is too long',
      ],
      email: '',
      emailRules: [
        (v) => !validation.isEmpty(v) || 'Email is required',
        (v) => validation.emailFormat(v) || 'Email format is invalid',
        (v) => validation.emailBlacklist(v) || 'Please provide your work email address.',
        (v) => validation.roleEmailBlacklist(v) || 'Role-based email addresses are not valid. Please use your individual company email address.',
        (v) => (v && v.length <= 250) || 'Email is too long',
      ],
      valid: false,
      loading: false,
    };
  },
  beforeCreate() {
    this.$emit('track-page-load', { page: 'Demo Request' });
  },
  methods: {
    ...mapActions(useWizard, ['updateFlash']),
    async submitForm() {
      try {
        const formValidateResult = await this.$refs.form.validate();
        if (formValidateResult.valid) {
          this.loading = true;
          const responseJSON = await signupCreateLeadUserDemo({
            email: this.email,
            firstName: this.firstName,
            lastName: this.lastName,
          });
          if (responseJSON.success === true) {
            if (responseJSON.has_company_info === true) {
              this.$router.push({
                name: 'demo-request-received',
                params: {
                  firstName: this.firstName,
                },
              });
            } else {
              this.$router.push({
                name: 'demo-about-your-company',
                params: {
                  firstName: this.firstName,
                  projectId: responseJSON.project_id,
                },
              });
            }
          }
        }
      } catch (e) {
        if (!e.message || e.message.charAt(0) !== '{') {
          this.updateFlash({ message: `Create lead user error: ${e.message}` });
          throw e;
        }
        const responseJSON = JSON.parse(e.message);
        if ( // If only error is please include corporate email
          responseJSON.errors
          && Object.keys(responseJSON.errors).length === 1
          && responseJSON.errors.email
          && responseJSON.errors.email.includes('Please use your corporate email address.')
        ) {
          this.$router.push({
            name: 'demo-provide-your-work-email',
            params: {
              firstName: this.firstName,
              lastName: this.lastName,
            },
          });
        } else if (
          responseJSON.errors
          && responseJSON.errors.email
          && responseJSON.errors.email
            .includes('Please sign up with your individual work email address.')
        ) {
          this.updateFlash({ message: 'Please sign up with your individual work email address.' });
        } else if (
          responseJSON.errors
          && Object.keys(responseJSON.errors).length === 1
          && responseJSON.errors.auth
          && responseJSON.errors.auth.includes('user already active.')
        ) {
          this.updateFlash({ template: 'user-active' });
        } else {
          this.updateFlash({ message: 'Something went wrong' });
        }
        throw e;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.form {
  width: 100%;
}

.paragraph {
  font-weight: 300;
  font-size: 20px !important;
  line-height: 30px !important;
  text-align: center;
  color: @text-darkest;
  margin: initial;
}
</style>
